import React from 'react';
import {
  Grid, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip, Box, TextField,
  Stack, RadioGroup, Radio, FormControlLabel, Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FloatingInputLabel } from '../../../components';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import FormSectionHeader from '../../../components/common/FormSectionHeader';
import SectionCard from '../../../components/common/SectionCard';
import { sa } from '../../../components/superAdminUi';
import { SELECTABLE_PRODUCT_NAMES, enforceSingleEiTier } from '../../../utils/Constants';

// Inputs and labels tokenised to the Super Admin design system.
const fieldSx = {
  '& .MuiInputBase-root': { fontFamily: sa.font.family, fontSize: '14px', color: sa.color.text },
  '& .MuiInputLabel-root': { fontFamily: sa.font.family, fontSize: '14px', color: sa.color.textGray },
  '& .MuiInputLabel-root.Mui-focused': { color: sa.color.teal },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.stroke },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
};

const radioSx = {
  color: sa.color.stroke,
  '&.Mui-checked': { color: sa.color.teal },
};

const radioLabelSx = {
  '& .MuiFormControlLabel-label': { fontFamily: sa.font.family, fontSize: 14, color: sa.color.text },
};

const TERMS = [
  { id: 1, name: '1 Year' },
  { id: 2, name: '2 Years' },
  { id: 3, name: '3 Years' },
];

const paymentModeName = (mode) => ({ 1: 'invoice', 2: 'card', 3: 'bank' })[mode || 1] || 'invoice';

const anniversary = (start, term, offsetDays = 0) => {
  const date = new Date(start);
  date.setFullYear(date.getFullYear() + term);
  date.setDate(date.getDate() + offsetDays);
  return date;
};

// Step 2: the subscription block, kept in parity with the old single-page form — term with
// auto End/Renewal dates, per-year costs, VAT/discount, payment method and the products
// multi-select with single-EI-tier enforcement.
export default function SubscriptionStep({ formik }) {
  const { values, handleChange, setFieldValue } = formik;

  const applyStart = (start) => {
    setFieldValue('StartDate', start);
    if (start && values.SubscriptionTerm) {
      setFieldValue('EndDate', anniversary(start, values.SubscriptionTerm, -1));
      setFieldValue('RenewalDate', anniversary(start, values.SubscriptionTerm, 0));
    }
  };

  const applyTerm = (term) => {
    setFieldValue('SubscriptionTerm', term);
    setFieldValue('IsMultiYear', term > 1);
    // Clear the cost of any year the term no longer covers, so a 1-year sub can't keep (and bill)
    // stale year-2/3 values left over from a longer term the operator picked first.
    if (term < 3) setFieldValue('Year3Cost', 0);
    if (term < 2) setFieldValue('Year2Cost', 0);
    if (values.StartDate) {
      setFieldValue('EndDate', anniversary(values.StartDate, term, -1));
      setFieldValue('RenewalDate', anniversary(values.StartDate, term, 0));
    }
  };

  const handleProducts = (event) => {
    const { value } = event.target;
    const next = typeof value === 'string' ? value.split(',') : value;
    setFieldValue('products', enforceSingleEiTier(next, values.products || []));
  };

  const term = parseInt(values.SubscriptionTerm, 10) || 1;
  const year1 = parseInt(values.Year1Cost, 10) || 0;
  const year2 = term >= 2 ? (parseInt(values.Year2Cost, 10) || 0) : 0;
  const year3 = term >= 3 ? (parseInt(values.Year3Cost, 10) || 0) : 0;
  const subtotal = year1 + year2 + year3;
  const total = subtotal + (subtotal * ((parseInt(values.VatPercentApplied, 10) || 0) / 100));

  return (
    <SectionCard title="Subscription & products" icon={<ReceiptLongOutlinedIcon fontSize="small" />}>
      <Grid container spacing={1.5}>
      <Grid item xs={12} lg={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Start Date"
            value={values.StartDate || null}
            onChange={applyStart}
            renderInput={(params) => <TextField {...params} fullWidth sx={fieldSx} />}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} lg={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="End Date"
            value={values.EndDate || null}
            onChange={(v) => setFieldValue('EndDate', v)}
            renderInput={(params) => <TextField {...params} fullWidth sx={fieldSx} />}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} lg={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Renewal Date"
            value={values.RenewalDate || null}
            onChange={(v) => setFieldValue('RenewalDate', v)}
            renderInput={(params) => <TextField {...params} fullWidth sx={fieldSx} />}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} lg={9} md={10}>
        <div className="cost-panel">
          <div><h4>Subscription Cost</h4></div>
          <div>
            <div className="label">Subscription Term</div>
            <FormControl fullWidth sx={{ marginTop: 1, ...fieldSx }}>
              <Select value={values.SubscriptionTerm} onChange={(e) => applyTerm(e.target.value)}>
                {TERMS.map((t) => (<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>))}
              </Select>
            </FormControl>
          </div>
          <div className="yearly-cost">
            <div>
              <div className="label">Year 1 Cost (£)</div>
              <FormControl><FloatingInputLabel label="Year 1 Cost" name="Year1Cost" value={values.Year1Cost} onChange={handleChange} /></FormControl>
            </div>
            {term >= 2 && (
              <div>
                <div className="label">Year 2 Cost (£)</div>
                <FormControl><FloatingInputLabel label="Year 2 Cost" name="Year2Cost" value={values.Year2Cost} onChange={handleChange} /></FormControl>
              </div>
            )}
            {term >= 3 && (
              <div>
                <div className="label">Year 3 Cost (£)</div>
                <FormControl><FloatingInputLabel label="Year 3 Cost" name="Year3Cost" value={values.Year3Cost} onChange={handleChange} /></FormControl>
              </div>
            )}
            <div>
              <div className="label">Discount (£)</div>
              <FormControl><FloatingInputLabel label="Discount" name="Discount" value={values.Discount} onChange={handleChange} /></FormControl>
            </div>
            <div>
              <div className="label">VAT (%)</div>
              <FormControl><FloatingInputLabel label="VAT (%)" name="VatPercentApplied" value={values.VatPercentApplied} onChange={handleChange} /></FormControl>
            </div>
          </div>
          <hr />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <p><strong>Final Total</strong></p>
            <p><strong>£{total}</strong></p>
          </Stack>
        </div>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mt: 1, fontFamily: sa.font.family, fontWeight: sa.font.bold, color: sa.color.text }}>
          Set Payment Method
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <RadioGroup
            value={paymentModeName(values.PaymentMode)}
            onChange={(e) => setFieldValue('PaymentMode', { invoice: 1, card: 2, bank: 3 }[e.target.value] || 1)}
          >
            <FormControlLabel value="invoice" control={<Radio sx={radioSx} />} label="Invoice" sx={radioLabelSx} />
            <FormControlLabel value="card" control={<Radio sx={radioSx} />} label="Pay via credit or debit card" sx={radioLabelSx} />
            <FormControlLabel value="bank" control={<Radio sx={radioSx} />} label="Pay via bank transfer" sx={radioLabelSx} />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormSectionHeader title="Products" />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="register-school-products-label">Select Products</InputLabel>
          <Select
            labelId="register-school-products-label"
            multiple
            value={values.products || []}
            onChange={handleProducts}
            input={<OutlinedInput label="Select Products" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (<Chip key={value} label={value} />))}
              </Box>
            )}
          >
            {SELECTABLE_PRODUCT_NAMES.map((item) => (
              <MenuItem key={item.id} value={item.name}>{item.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      </Grid>
    </SectionCard>
  );
}
